import React, { useState, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { fonts, colors, size } from '../../ColorFont/color';
import { IconArrowLeft, IconUpload, IconX } from '@tabler/icons-react';
import NewHeaderNavWithSidebar from '../../components/HeaderNav/NewHeaderNavWithSidebar';

const farmerTranslations = {
    en: {
        sellYourCrop: "Sell Your Crop",
        step1: "Step 1/6",
        addPhotosTitle: "Add Photos",
        addPhotosSub: "Good photos help buyers trust your listing",
        addPhotoBtn: "Add Photo",
        continue: "Continue"
    },
    hi: {
        sellYourCrop: "अपनी फसल बेचे",
        step1: "चरण 1/6",
        addPhotosTitle: "तस्वीरें जोड़ें",
        addPhotosSub: "अच्छी तस्वीरें खरीदारों को आपकी लिस्टिंग पर भरोसा करने में मदद करती हैं",
        addPhotoBtn: "तस्वीर जोड़ें",
        continue: "आगे बढ़ें"
    },
    mr: {
        sellYourCrop: "तुमचे पीक विका",
        step1: "पायरी 1/6",
        addPhotosTitle: "फोटो जोडा",
        addPhotosSub: "चांगले फोटो खरेदीदारांना तुमच्या लिस्टमधील उत्पादनावर विश्वास ठेवण्यास मदत करतात",
        addPhotoBtn: "फोटो जोडा",
        continue: "पुढे जा"
    }
};

const AddPlotsStep1 = ({
    setShowCard,
    setShowBtn,
    setAddPlots,
    userData,
    onFileCancel,
    setIsRender
}) => {
    const { i18n } = useTranslation();
    const langCode = i18n.language || 'en';
    const currentLang = langCode.startsWith('hi') ? 'hi' : langCode.startsWith('mr') || langCode.startsWith('mar') ? 'mr' : 'en';
    const tLocal = farmerTranslations[currentLang];

    const [selectedFiles, setSelectedFiles] = useState([]);
    const fileInputRef = useRef(null);

    const handleBackClick = () => {
        if (setAddPlots) setAddPlots(false);
        if (setShowBtn) setShowBtn(true);
        if (setShowCard) setShowCard(true);
        if (setIsRender) setIsRender(true);
        if (onFileCancel) onFileCancel();
    };

    const handleFileSelect = (event) => {
        const files = Array.from(event.target.files);
        setSelectedFiles((prev) => [...prev, ...files]);
    };

    const handleRemoveFile = (indexToRemove) => {
        setSelectedFiles((prev) => prev.filter((_, idx) => idx !== indexToRemove));
    };

    return (
        <Box sx={{
            width: '100%',
            minHeight: '100vh',
            backgroundColor: colors.white_bg || '#FFFFFF',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
        }}>
            {/* ======= TOP NAVIGATION BAR ======= */}
            <Box sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                height: { xs: '64px', md: '92px' },
                px: 0
            }}>
                <Box sx={{
                    width: '100%',
                    maxWidth: '1536px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    position: 'relative'
                }}>
                    <Box
                        onClick={handleBackClick}
                        sx={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: colors.background2
                            }
                        }}
                    >
                        <IconArrowLeft size={24} color={colors.text_dark || '#171C17'} />
                    </Box>

                    <Typography sx={{
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontFamily: fonts.poppins,
                        fontWeight: 600,
                        fontSize: '16px',
                        lineHeight: '24px',
                        letterSpacing: '-0.4px',
                        color: colors.text_dark || '#171C17'
                    }}>
                        {tLocal.sellYourCrop}
                    </Typography>

                    <Typography sx={{
                        fontFamily: fonts.poppins,
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        color: colors.text || '#6D786D'
                    }}>
                        {tLocal.step1}
                    </Typography>
                </Box>
            </Box>

            {/* ======= PROGRESS BAR ======= */}
            <Box sx={{ width: '100%', maxWidth: '1536px', px: 0 }}>
                <Box sx={{
                    width: '100%',
                    height: '4px',
                    backgroundColor: colors.step_bg || colors.primary_rgba_20,
                    borderRadius: '9999px',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    <Box sx={{
                        width: '16.666%', /* 1/6 completion */
                        height: '100%',
                        backgroundColor: colors.primary,
                        borderRadius: '9999px',
                        position: 'absolute',
                        left: 0,
                        top: 0
                    }} />
                </Box>
            </Box>
            <Box sx={{
                width: '100%',
                height: '1px',
                backgroundColor: colors.dashboard_card_border || '#E0E5EB',
                display: { xs: 'block', md: 'none' }
            }} />

            {/* ======= MAIN FORM CONTENT ======= */}
            <Box sx={{
                width: '100%',
                maxWidth: '480px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mt: { xs: '32px', md: '64px' },
                px: { xs: 2, md: 0 },
                pb: '160px' // Space for fixed bottom CTA
            }}>
                <Typography sx={{
                    fontFamily: fonts.poppins,
                    fontWeight: 700,
                    fontSize: '24px',
                    lineHeight: '32px',
                    letterSpacing: '-0.6px',
                    color: colors.text_dark || '#171C17',
                    textAlign: 'center',
                    mb: '8px'
                }}>
                    {tLocal.addPhotosTitle}
                </Typography>

                <Typography sx={{
                    fontFamily: fonts.poppins,
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '24px',
                    color: colors.text || '#6D786D',
                    textAlign: 'center',
                    mb: '48px'
                }}>
                    {tLocal.addPhotosSub}
                </Typography>

                {/* Upload Section Grid */}
                <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    gap: '16px',
                    width: '100%',
                    mb: 4
                }}>
                    {selectedFiles.map((file, index) => (
                        <Box key={index} sx={{
                            width: '152px',
                            height: '152px',
                            borderRadius: '24px',
                            position: 'relative',
                            overflow: 'hidden',
                            backgroundColor: colors.background2
                        }}>
                            <img
                                src={URL.createObjectURL(file)}
                                alt={`upload-${index}`}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                            <Box
                                onClick={() => handleRemoveFile(index)}
                                sx={{
                                    position: 'absolute',
                                    top: '8px',
                                    right: '8px',
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '50%',
                                    backgroundColor: '#EF4444',
                                    color: '#FFFFFF',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    boxShadow: '0px 2px 4px rgba(0,0,0,0.2)'
                                }}
                            >
                                <IconX size={14} stroke={3} />
                            </Box>
                        </Box>
                    ))}

                    {/* Add Photo Dashed Box */}
                    <Box
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                        sx={{
                            width: '152px',
                            height: '152px',
                            borderRadius: '24px',
                            border: `2px dashed ${colors.dashed_border || '#E3E8E3'}`,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            cursor: 'pointer',
                            backgroundColor: colors.white_bg || '#FFFFFF',
                            transition: 'all 0.2s',
                            '&:hover': {
                                backgroundColor: colors.background_light
                            }
                        }}
                    >
                        <IconUpload size={24} color={colors.text || '#6D786D'} stroke={1.5} />
                        <Typography sx={{
                            fontFamily: fonts.poppins,
                            fontWeight: 400,
                            fontSize: '12px',
                            lineHeight: '16px',
                            color: colors.text || '#6D786D',
                            mt: '4px'
                        }}>
                            {tLocal.addPhotoBtn}
                        </Typography>
                        <input
                            type="file"
                            multiple
                            accept="image/*"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            onChange={handleFileSelect}
                        />
                    </Box>
                </Box>

            </Box>

            {/* ======= FIXED BOTTOM CTA CONTAINER ======= */}
            <Box sx={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                backgroundColor: colors.white_bg || '#FFFFFF',
                borderTop: `1px solid ${colors.dashboard_card_border || '#E0E5EB'}`,
                padding: { xs: '16px', md: '24px' },
                zIndex: 10
            }}>
                <Box
                    component="button"
                    sx={{
                        width: '100%',
                        maxWidth: '480px',
                        height: '44px',
                        borderRadius: '24px',
                        backgroundColor: colors.primary,
                        color: colors.white_bg || '#FFFFFF',
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        fontFamily: fonts.poppins,
                        fontWeight: 600,
                        fontSize: '14px',
                        cursor: 'pointer',
                        boxShadow: colors.btn_shadow_upload || '0px 4px 16px -4px rgba(0, 0, 0, 0.1), 0px 2px 8px -2px colors.primary_rgba_10',
                        transition: 'background-color 0.2s',
                        '&:hover': {
                            backgroundColor: colors.primary_dark || '#149343'
                        }
                    }}
                >
                    {tLocal.continue} <IconArrowLeft size={16} style={{ transform: 'rotate(180deg)' }} />
                </Box>
            </Box>
        </Box>
    );
};

export default AddPlotsStep1;
